import { ComponentOverviewPageState } from './types';
import { EnhancedScopeData } from '@ardoq/data-model';
import {
  APIAttachment,
  APISurveyAttributes,
  ArdoqId,
  ComponentSurveyWithApprovalStatus,
} from '@ardoq/api-types';
import { DocumentArchiveAttachmentsState } from 'streams/documentArchive/types';
import type { BatchResult } from '@ardoq/api';
import { dateRangeOperations } from '@ardoq/date-range';
import { scopeDataOperations } from '@ardoq/scope-data';
import { SaveTagsSuccessPayload } from 'componentOverview2025/actions';
import { toByIdDictionary } from '@ardoq/common-helpers';
import {
  SetAllConnectionsScopeDataPayload,
  SetComponentOverviewDataPayload,
} from './actions';
import {
  SurveyComponentModalPayload,
  SurveyModalPayload,
} from 'components/SurveyModal/types';

const setComponentOverviewData = (
  state: ComponentOverviewPageState,
  { scopeData, componentId }: SetComponentOverviewDataPayload
): ComponentOverviewPageState => {
  const isNewComponent = state.componentId !== componentId;
  return {
    ...state,
    scopeData,
    componentId,
    hasError: false,
    ...(isNewComponent && { referencesTabScopeData: null }),
  };
};

const setAllConnectionsScopeData = (
  state: ComponentOverviewPageState,
  { allConnectionsScopeData, componentId }: SetAllConnectionsScopeDataPayload
): ComponentOverviewPageState => {
  const isNewComponent = state.componentId !== componentId;
  return {
    ...state,
    allConnectionsScopeData,
    componentId,
    ...(isNewComponent && { referencesTabScopeData: null }),
  };
};

const setReferencesTabScopeData = (
  state: ComponentOverviewPageState,
  { scopeData, componentId }: SetComponentOverviewDataPayload
): ComponentOverviewPageState => {
  if (state.componentId !== componentId) return state;
  return { ...state, referencesTabScopeData: scopeData };
};

const setComponentOverviewId = (
  state: ComponentOverviewPageState,
  componentId: ArdoqId | null
): ComponentOverviewPageState => {
  return {
    ...state,
    componentId,
    scopeData: null,
    allConnectionsScopeData: null,
    referencesTabScopeData: null,
    relevantSurveys: [],
    relevantSurveysLoaded: false,
    hasError: false,
    isUnavailable: false,
    currentTab: 'overview',
  };
};

const setRelevantSurveys = (
  state: ComponentOverviewPageState,
  relevantSurveys: ComponentSurveyWithApprovalStatus[]
): ComponentOverviewPageState => {
  return { ...state, relevantSurveys, relevantSurveysLoaded: true };
};

const setBusinessCriticalFieldIds = (
  state: ComponentOverviewPageState,
  businessCriticalFieldIds: string[]
): ComponentOverviewPageState => {
  return {
    ...state,
    businessCriticalFieldIds,
  };
};

const setComponentOverviewPageError = (
  state: ComponentOverviewPageState
): ComponentOverviewPageState => {
  return { ...state, hasError: true };
};

const setComponentOverviewPageUnavailable = (
  state: ComponentOverviewPageState
): ComponentOverviewPageState => {
  return { ...state, isUnavailable: true };
};

const applyBatchResult = (
  scopeData: EnhancedScopeData,
  result: BatchResult
): EnhancedScopeData => {
  const updatedComponentsById = {
    ...scopeData.componentsById,
    ...result.components.updated,
  };
  const updatedReferencesById = {
    ...scopeData.referencesById,
    ...result.references.updated,
  };
  const {
    fields,
    attachments,
    models,
    scopeComponentIds,
    workspaces,
    tags,
    users,
    dateRangeFieldMap,
  } = scopeData;
  const components = Object.values(updatedComponentsById).map(entity =>
    dateRangeOperations.mergeDateRangeFieldsForEntity(
      entity,
      scopeData.dateRangeFieldMap
    )
  );
  const references = Object.values(updatedReferencesById).map(entity =>
    dateRangeOperations.mergeDateRangeFieldsForEntity(
      entity,
      scopeData.dateRangeFieldMap
    )
  );
  return scopeDataOperations.enhance({
    fields,
    attachments,
    models,
    workspaces,
    tags,
    users,
    dateRangeFieldMap,
    scopeComponents: Array.from(scopeComponentIds),
    components,
    references,
  });
};

const fieldValueUpdateSuccess = (
  state: ComponentOverviewPageState,
  result: BatchResult
): ComponentOverviewPageState => {
  const { scopeData, allConnectionsScopeData, referencesTabScopeData } = state;
  if (!scopeData) return state;
  return {
    ...state,
    scopeData: applyBatchResult(scopeData, result),
    allConnectionsScopeData: allConnectionsScopeData
      ? applyBatchResult(allConnectionsScopeData, result)
      : null,
    referencesTabScopeData: referencesTabScopeData
      ? applyBatchResult(referencesTabScopeData, result)
      : null,
  };
};

const applyTagsResult = (
  scopeData: EnhancedScopeData,
  { create, update, deleted }: SaveTagsSuccessPayload
): EnhancedScopeData => {
  const newTags = [
    ...scopeData.tags.filter(tag => !deleted.includes(tag._id)),
    ...create,
  ].map(tag => {
    const updatedTagInUpdate = update.find(t => t._id === tag._id);
    return updatedTagInUpdate ? updatedTagInUpdate : tag;
  });
  return {
    ...scopeData,
    tags: newTags,
    tagsById: toByIdDictionary(newTags),
  };
};

const saveTagsSuccess = (
  state: ComponentOverviewPageState,
  payload: SaveTagsSuccessPayload
): ComponentOverviewPageState => {
  const { scopeData, allConnectionsScopeData, referencesTabScopeData } = state;
  if (!scopeData) return state;
  return {
    ...state,
    scopeData: applyTagsResult(scopeData, payload),
    allConnectionsScopeData: allConnectionsScopeData
      ? applyTagsResult(allConnectionsScopeData, payload)
      : null,
    referencesTabScopeData: referencesTabScopeData
      ? applyTagsResult(referencesTabScopeData, payload)
      : null,
  };
};

const surveyIdFromModalPayload = (
  payload: SurveyModalPayload
): ArdoqId | null => payload.survey?._id ?? payload.surveyId ?? null;

const toSurveyToOpen = (
  {
    componentId,
    scopeData,
    relevantSurveys,
    relevantSurveysLoaded,
  }: ComponentOverviewPageState,
  surveyId: ArdoqId
): SurveyComponentModalPayload | null => {
  if (!componentId || !scopeData || !relevantSurveysLoaded) return null;
  const component = scopeData.componentsById[componentId];
  const survey = relevantSurveys.find(({ _id }) => _id === surveyId);
  if (!component || !survey) return null;
  return { survey, componentId, componentName: component.name };
};

const shouldShowSurveyInComponentOverview = (
  survey: APISurveyAttributes
): boolean => {
  return survey.discoverEnabled || false;
};

const mergeAttachmentsById = (
  existing: APIAttachment[],
  incoming: APIAttachment[]
): APIAttachment[] => {
  const byId = new Map(existing.map(a => [a._id, a]));
  incoming.forEach(a => byId.set(a._id, a));
  return Array.from(byId.values());
};

const updateAttachments = (
  state: ComponentOverviewPageState,
  { models }: DocumentArchiveAttachmentsState
): ComponentOverviewPageState => {
  if (!state.scopeData) return state;
  const mergedAttachments = mergeAttachmentsById(
    state.scopeData.attachments,
    models
  );
  return {
    ...state,
    scopeData: { ...state.scopeData, attachments: mergedAttachments },
    referencesTabScopeData: state.referencesTabScopeData
      ? { ...state.referencesTabScopeData, attachments: mergedAttachments }
      : null,
  };
};

export const componentOverviewPageOperations = {
  setComponentOverviewData,
  setAllConnectionsScopeData,
  setReferencesTabScopeData,
  setComponentOverviewId,
  setRelevantSurveys,
  setBusinessCriticalFieldIds,
  setComponentOverviewPageError,
  setComponentOverviewPageUnavailable,
  fieldValueUpdateSuccess,
  saveTagsSuccess,
  surveyIdFromModalPayload,
  toSurveyToOpen,
  shouldShowSurveyInComponentOverview,
  updateAttachments,
};
